import { Flex, IconButton, Tooltip, useColorModeValue, Icon, Box, Divider } from "@chakra-ui/react"
import { MarkupTool } from "./videoMarkupEditor"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faDrawPolygon, faMapPin, faMousePointer } from "@fortawesome/free-solid-svg-icons"

const TriangleIcon = (props: any) => (
    <Icon viewBox="0 0 24 24" {...props}>
        <polygon points="12,4 20,20 4,20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
    </Icon>
)

const RectangleIcon = (props: any) => (
    <Icon viewBox="0 0 24 24" {...props}>
        <rect x="4" y="5" width="16" height="14" rx="2.5" fill="none" stroke="currentColor" strokeWidth="2" />
    </Icon>
)

const PentagonIcon = (props: any) => (
    <Icon viewBox="0 0 24 24" {...props}>
        <polygon points="12,3.5 21,10 18,20 6,20 3,10" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
    </Icon>
)

const CircleIcon = (props: any) => (
    <Icon viewBox="0 0 24 24" {...props}>
        <circle cx="12" cy="12" r="8" fill="none" stroke="currentColor" strokeWidth="2" />
    </Icon>
)

const EllipseIcon = (props: any) => (
    <Icon viewBox="0 0 24 24" {...props}>
        <ellipse cx="12" cy="12" rx="9" ry="5.5" fill="none" stroke="currentColor" strokeWidth="2" />
    </Icon>
)

const ArrowIcon = (props: any) => (
    <Icon viewBox="0 0 24 24" {...props}>
        <path d="M5 12h14M13 6l6 6-6 6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </Icon>
)

const LineIcon = (props: any) => (
    <Icon viewBox="0 0 24 24" {...props}>
        <line x1="5" y1="19" x2="19" y2="5" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
    </Icon>
)

const TextIcon = (props: any) => (
    <Icon viewBox="0 0 24 24" {...props}>
        <text x="6" y="17.5" fontSize="17" fontWeight="bold" fontFamily="system-ui, sans-serif" fill="currentColor">T</text>
    </Icon>
)

const MarkupToolbar: React.FC<{
    tool: MarkupTool,
    setTool: React.Dispatch<React.SetStateAction<MarkupTool>>,
    orientation?: 'horizontal' | 'vertical',
}> = ({ tool, setTool, orientation = 'vertical' }) => {
    const isHorizontal = orientation === 'horizontal';
    const bgColor = useColorModeValue("rgba(255, 255, 255, 0.94)", "rgba(24, 24, 27, 0.94)");
    const borderColor = useColorModeValue("gray.200", "gray.700");
    const activeBg = useColorModeValue("#10b981", "#10b981");
    const activeColor = "white";
    const inactiveColor = useColorModeValue("gray.600", "gray.300");
    const hoverBg = useColorModeValue("gray.100", "gray.750");
    const dividerColor = useColorModeValue("gray.200", "gray.700");

    const toolGroups: Array<{
        group: string;
        items: Array<{ id: MarkupTool; label: string; icon: React.ReactElement }>;
    }> = [
        {
            group: 'select',
            items: [
                { id: 'pointer', label: 'Select & Move (V)', icon: <FontAwesomeIcon icon={faMousePointer} style={{ fontSize: '13px' }} /> },
            ]
        },
        {
            group: 'draw',
            items: [
                { id: 'polygon', label: 'Freeform Polygon (P)', icon: <FontAwesomeIcon icon={faDrawPolygon} style={{ fontSize: '13px' }} /> },
                { id: 'hotspot', label: 'Point Hotspot (H)', icon: <FontAwesomeIcon icon={faMapPin} style={{ fontSize: '13px' }} /> },
            ]
        },
        {
            group: 'shapes',
            items: [
                { id: 'triangle', label: 'Triangle Shape', icon: <TriangleIcon fontSize="16px" /> },
                { id: 'rectangle', label: 'Rectangle Box', icon: <RectangleIcon fontSize="16px" /> },
                { id: 'pentagon', label: 'Pentagon Shape', icon: <PentagonIcon fontSize="16px" /> },
                { id: 'circle', label: 'Circle Shape', icon: <CircleIcon fontSize="16px" /> },
                { id: 'ellipse', label: 'Ellipse Shape', icon: <EllipseIcon fontSize="16px" /> },
            ]
        },
        {
            group: 'annotations',
            items: [
                { id: 'arrow', label: 'Directional Arrow', icon: <ArrowIcon fontSize="16px" /> },
                { id: 'line', label: 'Straight Line', icon: <LineIcon fontSize="16px" /> },
                { id: 'text', label: 'Text Callout (T)', icon: <TextIcon fontSize="16px" /> },
            ]
        }
    ];

    return (
        <Flex
            bg={bgColor}
            backdropFilter="blur(20px)"
            borderRadius={isHorizontal ? "full" : "2xl"}
            border="1px solid"
            borderColor={borderColor}
            boxShadow={isHorizontal ? "0 4px 20px rgba(0,0,0,0.06)" : "0 12px 36px rgba(0,0,0,0.18)"}
            p={1.5}
            gap={1}
            direction={isHorizontal ? "row" : "column"}
            w="fit-content"
            align="center"
            zIndex={30}
        >
            {toolGroups.map((group, groupIdx) => (
                <Flex key={group.group} direction={isHorizontal ? "row" : "column"} align="center" gap={1}>
                    {groupIdx > 0 && (
                        <Box
                            w={isHorizontal ? "1px" : "18px"}
                            h={isHorizontal ? "18px" : "1px"}
                            bg={dividerColor}
                            mx={isHorizontal ? 0.5 : 0}
                            my={isHorizontal ? 0 : 0.5}
                        />
                    )}
                    {group.items.map(t => {
                        const isActive = tool === t.id;
                        return (
                            <Tooltip
                                key={t.id}
                                label={t.label}
                                placement={isHorizontal ? "top" : "right"}
                                hasArrow
                                openDelay={150}
                                bg="gray.900"
                                color="white"
                                fontSize="2xs"
                                fontWeight="600"
                                borderRadius="lg"
                                px={2.5}
                                py={1}
                            >
                                <IconButton
                                    aria-label={t.label}
                                    icon={t.icon}
                                    onClick={() => setTool(t.id)}
                                    variant="ghost"
                                    bg={isActive ? activeBg : "transparent"}
                                    color={isActive ? activeColor : inactiveColor}
                                    boxShadow={isActive ? "0 2px 10px rgba(16, 185, 129, 0.45)" : "none"}
                                    borderRadius={isHorizontal ? "full" : "xl"}
                                    size="xs"
                                    w={isHorizontal ? "30px" : "32px"}
                                    h={isHorizontal ? "30px" : "32px"}
                                    minW={isHorizontal ? "30px" : "32px"}
                                    _hover={isActive ? {} : { bg: hoverBg, transform: isHorizontal ? 'translateY(-1px)' : 'scale(1.1)' }}
                                    _active={{ bg: activeBg, transform: "scale(0.95)" }}
                                    transition="all 0.15s cubic-bezier(0.4, 0, 0.2, 1)"
                                />
                            </Tooltip>
                        );
                    })}
                </Flex>
            ))}
        </Flex>
    );
};

export default MarkupToolbar;